'use client'

import { useBooleanDebounce } from '@/hooks/use-boolean-debounce'
import { useQueryParams } from '@/hooks/use-query-params'
import { useMutation } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { KeyboardEvent, useEffect, useRef, useState } from 'react'

import { Icon } from '@/components'
import { LoddingIcon } from '@/components/icon-svg'
import { useDebounce } from '@/hooks/use-debounce'
import { getVND } from '@/utils/helpers'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from '@/features/ref/components/Link'
//MUI
import { fetchProducts } from '@/features/products/services'
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  InputBase,
  Paper,
  Stack,
  styled,
  Typography,
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { ClearIcon } from '@mui/x-date-pickers/icons'
import { PopularSearch } from './PopularSearch'
import { SearchType } from './SearchType'
const SAMPLE_TOP_KEYWORDS = [
  {
    query_text: 'Áo thun',
    popularity: 428,
    current_popularity: 0,
    previous_popularity: 0,
  },
  {
    query_text: 'Quần jeans',
    popularity: 330,
    current_popularity: 0,
    previous_popularity: 0,
  },
  {
    query_text: 'Áo sơ mi',
    popularity: 233,
    current_popularity: 0,
    previous_popularity: 0,
  },
  {
    query_text: 'Polo nam',
    popularity: 180,
    current_popularity: 0,
    previous_popularity: 0,
  },
]
const LIST_TYPE_PRODUCT = [{ label: 'Quần áo', value: 10 }]

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  marginLeft: 3,
  boxShadow: 'none',
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
    minWidth: 'auto',
    padding: '6px',
    '& .MuiButton-startIcon': {
      margin: 0,
    },
  },
}))

interface StyledSearchPaperProps {
  isFocused?: boolean
}

const StyledSearchPaper = styled(Paper)<StyledSearchPaperProps>(({ theme, isFocused }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minWidth: '250px',
  boxShadow: 'none',
  background: 'transparent',
  border: '1px solid #C2C2C2',
  transition: 'all 0.2s ease-out',
  '& form': {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
  },
  padding: '4px 4px 4px 16px',
  borderRadius: '8px',
  [theme.breakpoints.down('sm')]: {
    borderRadius: '4px',
    padding: '4px 4px 4px 8px',
    width: '100%',
    position: 'relative',
    '& .MuiInputBase-root': {
      transition: 'all 0.3s ease-out',
      transitionDelay: '0.1s',
      marginLeft: isFocused ? 0 : undefined,
      width: '100%',
    },
  },
}))

export const SearchBar = () => {
  const t = useTranslations()
  const [value, setValue] = useState<string>('')
  const [product, setProduct] = useState<IProducts>()
  const [isInputFocused, setIsInputFocused] = useState(false)
  const debouncedOpen = useBooleanDebounce(isInputFocused, 300)
  const debounceValue = useDebounce(value, 300)
  const inputRef = useRef<HTMLInputElement>(null)
  const router = useRouter()
  const { createQueryString } = useQueryParams()

  const { mutate: getProduct, isPending } = useMutation({
    mutationKey: ['product', 'auto-complete'],
    mutationFn: async (valueSearch: string) => {
      return await fetchProducts({ search: valueSearch, pageSize: 4 })
    },
    onSuccess: (data) => {
      setProduct(data ?? ({} as IProducts))
    },
  })

  useEffect(() => {
    if (!value) {
      // setProduct(listProduct ?? ({} as IProducts))
      return
    }
    setProduct(undefined)
    getProduct(debounceValue as string)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceValue])

  const handleChangeSearch = (e: string) => {
    setValue(e)
  }

  const handleSearchPopular = (e: string) => {
    handleChangeSearch(e)
    inputRef?.current?.focus()
  }

  const handleSearch = () => {
    if (!value) return
    const kw = createQueryString('search', value)
    router.push(`/danh-muc-san-pham?${kw}`)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    e.code == 'Enter' && handleSearch()
  }

  return (
    <Stack spacing={0.5}>
      <Autocomplete
        freeSolo
        className="!h-full w-full"
        onInputChange={(_, newValue) => setValue(newValue)}
        options={product?.items || []}
        filterOptions={(x) => x}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option?.name || '')}
        loading={isPending}
        open={debouncedOpen}
        onClose={() => setIsInputFocused(false)}
        blurOnSelect
        sx={{
          '& .MuiFormControl-root': {
            height: '100%',
            width: '100%',
          },
        }}
        loadingText={
          <div className="my-5 flex items-center justify-center">
            {isPending ? <LoddingIcon /> : t('not-found')}
          </div>
        }
        clearIcon={null}
        noOptionsText={
          <div className="my-5 flex items-center justify-center">
            {isPending ? <LoddingIcon /> : t('not-found')}
          </div>
        }
        renderInput={(params) => (
          <StyledSearchPaper>
            <SearchType isFocused={isInputFocused} options={LIST_TYPE_PRODUCT} />
            <InputBase
              {...params.InputProps}
              inputRef={inputRef}
              inputProps={{
                ...params.inputProps,
                'aria-label': 'Tìm kiếm mặt hàng',
                onFocus: () => {
                  setIsInputFocused(true)
                },
                value: value,
                onBlur: (e) => {
                  // Only blur if we're not clicking inside the dropdown
                  if (!e.relatedTarget || !e.relatedTarget.closest('.MuiAutocomplete-popper')) {
                    setIsInputFocused(false)
                  }
                },
                onKeyDown: handleKeyDown,
              }}
              sx={{
                flex: 1,
                paddingRight: '12px!important',
                fontSize: {
                  xs: 14,
                  md: 16,
                },
              }}
              placeholder="Tìm kiếm mặt hàng"
              endAdornment={
                value ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => handleChangeSearch('')} edge="end">
                      <ClearIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            />
            <Stack direction="row" spacing={{ xs: 1, md: 2, lg: 3 }} alignItems="center">
              {/* <Icon.Camera /> */}
              <StyledButton
                onClick={() => setValue('')}
                role={undefined}
                variant="contained"
                tabIndex={-1}
                startIcon={<Icon.Search />}
              >
                <Box component={'span'} sx={{ display: { xs: 'none', md: 'inline' } }}>
                  {t('search')}
                </Box>
              </StyledButton>
            </Stack>
          </StyledSearchPaper>
        )}
        renderOption={(props, option) => (
          <li {...props} key={option?.sku}>
            <Link
              href={`/products/${option.url_key}`}
              style={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <Image alt={option.name || 'Ảnh sản phẩm'} src={option.image?.url} width={60} height={40} />
              <div>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    lineClamp: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {option.name}
                </Typography>
                <Typography variant="body2">
                  {getVND(option.price_range?.maximum_price?.final_price?.value)}
                </Typography>
              </div>
            </Link>
          </li>
        )}
      />
      <PopularSearch
        items={SAMPLE_TOP_KEYWORDS}
        translatedTitle={t('most_searched')}
        onSelect={handleSearchPopular}
      />
    </Stack>
  )
}
